// ========== Utilities ==========
function toUuidString(bytes: Uint8Array): string {
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
  return (
    hex.slice(0, 8) +
    '-' +
    hex.slice(8, 12) +
    '-' +
    hex.slice(12, 16) +
    '-' +
    hex.slice(16, 20) +
    '-' +
    hex.slice(20, 32)
  )
}

// Convert unsigned BigInt to fixed-length big-endian byte array via modulo/division (no bitwise literals)
function bigIntToBytesBE(x: bigint, len: number): Uint8Array {
  let n = BigInt(x)
  const out = new Uint8Array(len)
  const TWO_FIVE_SIX = BigInt(256)
  for (let i = len - 1; i >= 0; i--) {
    out[i] = Number(n % TWO_FIVE_SIX)
    n = n / TWO_FIVE_SIX
  }
  if (n !== BigInt(0)) {
    throw new Error('Overflow: value does not fit in the requested length.')
  }
  return out
}

// base36 parser (0-9, a-z, A-Z)
function parseBase36BigInt(s: string): bigint {
  let n = BigInt(0)
  for (let i = 0; i < s.length; i++) {
    const ch = s.charCodeAt(i)
    let v
    if (ch >= 48 && ch <= 57) {
      v = ch - 48 // '0'..'9'
    } else {
      const lc = ch | 0x20 // lowercased
      if (lc >= 97 && lc <= 122) {
        // 'a'..'z'
        v = 10 + (lc - 97)
      } else {
        throw new Error('Invalid CUID: non-base36 character encountered.')
      }
    }
    n = n * BigInt(36) + BigInt(v)
  }
  return n
}

// Helper: 2^k as BigInt, avoiding BigInt literal suffixes
function pow2(k: number): bigint {
  let r = BigInt(1)
  // shift in chunks to avoid large intermediate numbers in one go
  while (k > 0) {
    const step = Math.min(30, k) // 2^30 per step is fine
    r = r << BigInt(step)
    k -= step
  }
  return r
}

// ========== Firebase UID → UUID ==========
// Mirrors Elixir: base62 decode → little-endian bytes → take first 16 → UUID string
function firebaseUidToUuid(uid: string): string {
  if (typeof uid !== 'string') {
    throw new TypeError('uid must be a string')
  }

  let acc = BigInt(0)
  for (let i = 0; i < uid.length; i++) {
    const code = uid.charCodeAt(i)
    let v
    if (code >= 0x30 && code <= 0x39) {
      v = BigInt(code - 0x30) // '0'..'9'
    } else if (code >= 0x41 && code <= 0x5a) {
      v = BigInt(code - 0x41 + 10) // 'A'..'Z'
    } else if (code >= 0x61 && code <= 0x7a) {
      v = BigInt(code - 0x61 + 36) // 'a'..'z'
    } else {
      const bad = uid[i]
      throw new TypeError(`non-alphabet digit found: ${bad}`)
    }
    acc = acc * BigInt(62) + v
  }

  // minimal-length little-endian bytes of the integer
  const bytes = []
  if (acc === BigInt(0)) {
    bytes.push(0)
  } else {
    const TWO_FIVE_SIX = BigInt(256)
    while (acc > BigInt(0)) {
      bytes.push(Number(acc % TWO_FIVE_SIX))
      acc = acc / TWO_FIVE_SIX
    }
  }

  if (bytes.length < 16) {
    throw new Error('decoded value is <16 bytes; cannot form UUID')
  }

  const idBytes = bytes.slice(0, 16) // lowest 128 bits
  return toUuidString(Uint8Array.from(idBytes))
}

// ========== CUID → UUID (UUIDv7-compatible) ==========
function decodeCuidToUuidBytes(cuid: string): Uint8Array {
  if (typeof cuid !== 'string' || cuid.length !== 25 || cuid[0] !== 'c') {
    throw new Error('Invalid CUID: expected "c" + 8 + 4 + 12 base36 chars')
  }

  const timestampStr = cuid.slice(1, 1 + 8) // 8
  const counterStr = cuid.slice(9, 9 + 4) // 4
  const randomStr = cuid.slice(13) // 12

  const ts = parseBase36BigInt(timestampStr)
  const ctr = parseBase36BigInt(counterStr)
  const rnd = parseBase36BigInt(randomStr)

  const MAX48 = pow2(48) - BigInt(1)
  const MAX12 = pow2(12) - BigInt(1)
  const MAX62 = pow2(62) - BigInt(1)

  if (ts > MAX48 || ctr > MAX12 || rnd > MAX62) {
    throw new Error('Invalid CUID: component out of range (ts:48b, counter:12b, random:62b).')
  }

  // Assemble: [48b ts][4b v7][12b ctr][2b variant=2][62b rnd]
  let acc = ts
  acc = acc * pow2(4) + BigInt(7) // version 7
  acc = acc * pow2(12) + ctr // counter
  acc = acc * pow2(2) + BigInt(2) // variant RFC4122 (0b10)
  acc = acc * pow2(62) + rnd // random

  return bigIntToBytesBE(acc, 16)
}

function cuidToUUID(cuid: string): string {
  return toUuidString(decodeCuidToUuidBytes(cuid))
}

export function upgradeLegacyId(id: string): string {
  if (id.length == 25) {
    return cuidToUUID(id)
  } else if (id.length == 28) {
    return firebaseUidToUuid(id)
  } else {
    return id
  }
}
